import React, {
  useRef,
  useState,
  useCallback,
  PropsWithChildren,
  createContext,
  useContext,
} from "react";
import { Drawer } from "@mui/material";
import { FormContext } from "../context/formContext";
import SlideUnsaved from "@common/components/SlideUnsaved";

export const TicketExpandingDrawerContext = createContext<{
  animate: (extend: boolean) => void;
}>({ animate: () => {} });
const TicketExpandingDrawer = ({
  open = false,
  onClose,
  children,
}: PropsWithChildren<{
  open?: boolean;
  onClose: () => void;
}>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [heightDrawer, setHeightDrawer] = useState(0);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [isFormModified, setIsFormModified] = useState<boolean>(false);
  const { form } = useContext(FormContext);

  const animate = useCallback(
    (extend: boolean) => {
      const domNode = ref.current;
      if (domNode === null || extend === false) return;

      requestAnimationFrame(() => {
        domNode.style.height = "85%";
        domNode.style.maxHeight = "85%";
        domNode.style.transitionDuration = "0s";
        domNode.style.transform = `translateY(${
          domNode.offsetHeight - heightDrawer
        }px)`;

        requestAnimationFrame(() => {
          domNode.style.transitionDuration = "0.2s";
          domNode.style.transform = `translateY(0px)`;
        });
      });
    },
    [ref.current]
  );

  const handleClose = () => {
    onClose();
    setShowWarning(false);
    setIsFormModified(false);
  };

  const handleCloseWithWarning = () => {
    if (isFormModified) {
      setShowWarning(true);
      return;
    }
    if (form.hasChanged) {
      setShowWarning(true);
    } else {
      handleClose();
    }
    setIsFormModified(form.hasChanged ?? false);
  };

  return (
    <Drawer
      open={open}
      onClose={handleCloseWithWarning}
      anchor="bottom"
      sx={{
        "& .MuiDrawer-paper": {
          paddingBottom: 2,
          backgroundImage:
            "linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15))",
        },
        "& .MuiDivider-root": {
          mx: 3,
        },
      }}
      slotProps={{
        paper: {
          ref: ref,
          sx: { maxHeight: "85%" },
        },

        transition: {
          onEntering: (node, isAppearing) => {
            setHeightDrawer(node.offsetHeight);
          },
        }
      }}>
      <TicketExpandingDrawerContext.Provider value={{ animate }}>
        {children}
      </TicketExpandingDrawerContext.Provider>
      <SlideUnsaved
        sx={{
          py: 2,
        }}
        direction="left"
        warning={showWarning}
        onClose={handleClose}
        onCancel={() => setShowWarning(false)}
      />
    </Drawer>
  );
};

export default TicketExpandingDrawer;
